import { ActivityDayDetails, ActivityNbDayDetails } from '@bloomays-lib/types.shared';
import {
  dateFormat,
  isHoliday,
  isSaturday,
  isSunday,
  isValidDate,
  isWeekEndDay,
  lastDateOfMonth,
  weekDay,
} from './date.utils';
import { isSameMonth } from 'date-fns/fp/isSameMonth';
import { isBefore } from 'date-fns/fp/isBefore';
import { isAfter } from 'date-fns/fp/isAfter';
import { isSameDay } from 'date-fns/fp/isSameDay';

type MissionBoundaries = {
  beginMission: Date;
  endMission: Date;
};

export const retrieveActivityDetailValue = (
  activity: ActivityNbDayDetails,
  day: number,
  property: 'isAstreinte' | 'duration' | 'comment',
): number | undefined | string => {
  if (activity === undefined) return;
  const objectLength = Object.keys(activity).length;
  if (objectLength === 0) return;
  switch (typeof activity[day]) {
    case 'number':
    case 'undefined':
    case null:
      switch (property) {
        case 'duration':
          // legacy format
          return (activity[day] || 0) as unknown as number;
        case 'comment':
          return '';
        case 'isAstreinte':
          return 0;
      }
      break;
  }
  return activity[day]?.[property] ? activity[day][property] : property === 'comment' ? '' : 0;
};

export const convertActivityToEachDaysArray = (month: string, activityDaysDetails: string): ActivityDayDetails[] => {
  let activityNbDaysDetails: ActivityNbDayDetails;
  try {
    activityNbDaysDetails = JSON.parse(activityDaysDetails);
  } catch (err: any) {
    throw new Error(`Activity is not a valid JSON - Activity: ${activityDaysDetails}`, {
      cause: err,
    });
  }

  const lastDateCurrentMonth = lastDateOfMonth(new Date(month));

  const arrayDateofEachDay: ActivityDayDetails[] = [];
  for (let i = lastDateCurrentMonth.getDate(); i >= 1; i--) {
    const journey = `${lastDateCurrentMonth.getMonth() + 1}/${i}/${lastDateCurrentMonth.getFullYear()}`;

    const journeyStringToDate = new Date(journey);
    const date = dateFormat(journeyStringToDate, 'dd/MM/yyyy', 'fr');

    arrayDateofEachDay.push({
      id: i,
      day: weekDay[journeyStringToDate.getDay()],
      date: date,
      isHoliday: isHoliday(journeyStringToDate),
      isWeekEndDay: isWeekEndDay(journeyStringToDate),
      isSaturday: isSaturday(journeyStringToDate),
      isSunday: isSunday(journeyStringToDate),
      dayWorked: (retrieveActivityDetailValue(activityNbDaysDetails, i, 'duration') as number) || 0,
      checked: false,
      comment: (retrieveActivityDetailValue(activityNbDaysDetails, i, 'comment') as string) || '',
      astreinte: (retrieveActivityDetailValue(activityNbDaysDetails, i, 'isAstreinte') as number) || 0,
    });
  }

  return arrayDateofEachDay.reverse();
};

export const convertActivityToEachDaysArrayWithMissionBoundaries = (
  month: string,
  activityDaysDetails: string,
  missionBoundaries: MissionBoundaries,
): ActivityDayDetails[] => {
  let activityNbDaysDetails: ActivityNbDayDetails;
  try {
    activityNbDaysDetails = JSON.parse(activityDaysDetails);
  } catch (err: any) {
    throw new Error(`Activity is not a valid JSON - Activity: ${activityDaysDetails}`, {
      cause: err,
    });
  }

  const monthDate = new Date(month);
  const lastDateCurrentMonth = lastDateOfMonth(monthDate);
  const isMonthOfMissionBegin = isSameMonth(missionBoundaries.beginMission, monthDate);
  const isMonthOfMissionEnd = isSameMonth(missionBoundaries.endMission, monthDate);

  const arrayDateofEachDay: ActivityDayDetails[] = [];
  for (let i = lastDateCurrentMonth.getDate(); i >= 1; i--) {
    const journey = `${lastDateCurrentMonth.getMonth() + 1}/${i}/${lastDateCurrentMonth.getFullYear()}`;

    const journeyStringToDate = new Date(journey);
    // Exclure les jours avant le début de mission pour le mois de début
    if (
      isMonthOfMissionBegin &&
      !isSameDay(missionBoundaries.beginMission, journeyStringToDate) &&
      isBefore(missionBoundaries.beginMission, journeyStringToDate)
    ) {
      continue;
    }

    // Exclure les jours après la fin de mission pour le mois de fin
    if (isMonthOfMissionEnd && isAfter(missionBoundaries.endMission, journeyStringToDate)) {
      continue;
    }

    const date = dateFormat(journeyStringToDate, 'dd/MM/yyyy', 'fr');

    arrayDateofEachDay.push({
      id: i,
      day: weekDay[journeyStringToDate.getDay()],
      date: date,
      isHoliday: isHoliday(journeyStringToDate),
      isWeekEndDay: isWeekEndDay(journeyStringToDate),
      isSaturday: isSaturday(journeyStringToDate),
      isSunday: isSunday(journeyStringToDate),
      dayWorked: (retrieveActivityDetailValue(activityNbDaysDetails, i, 'duration') as number) || 0,
      checked: false,
      comment: (retrieveActivityDetailValue(activityNbDaysDetails, i, 'comment') as string) || '',
      astreinte: (retrieveActivityDetailValue(activityNbDaysDetails, i, 'isAstreinte') as number) || 0,
    });
  }

  return arrayDateofEachDay.reverse();
};

export const createDaysDetail = (days: ActivityDayDetails[]): ActivityNbDayDetails => {
  if (!days || days.length === 0) {
    throw new Error('Pas de détail du CRA');
  }
  const details: ActivityNbDayDetails = {};

  days.forEach((day: ActivityDayDetails) => {
    details[day.id] = {
      duration: day.dayWorked || 0,
      comment: day.comment || '',
      isAstreinte: day.astreinte || 0,
    };
  });
  return details;
};

export const concatEmbedSignature = (signDate: Date, signerIPAddress: string | undefined, signerIdentity: string) => {
  return `${signDate.toISOString()}|${signerIPAddress}|${signerIdentity}`;
};

export const splitEmbedSignature = (
  embedSignature: string,
): { signDate: Date | undefined; signerIPAddress: string | undefined; signerIdentity: string } => {
  const splited = embedSignature.split('|');
  return {
    signDate: isValidDate(splited[0]) ? new Date(splited[0]) : undefined,
    signerIPAddress: splited[1] || undefined,
    signerIdentity: splited[2] || '',
  };
};

type ZeroCRALike = {
  nbDaysWorked?: number | null;
  nbDaysMarkupWorked?: number | null;
  nbDaysMarkup2Worked?: number | null;
  astreinte?: number | null;
};

export const isZeroCRAActivity = (activity: ZeroCRALike): boolean => {
  const nbDaysWorked = activity.nbDaysWorked || 0;
  const nbDaysMarkupWorked = activity.nbDaysMarkupWorked || 0;
  const nbDaysMarkup2Worked = activity.nbDaysMarkup2Worked || 0;
  const astreinte = activity.astreinte || 0;

  return nbDaysWorked + nbDaysMarkupWorked + nbDaysMarkup2Worked + astreinte === 0;
};
